/**
 * ErrorBoundary Component
 *
 * A React error boundary that catches JavaScript errors in child component
 * trees, logs the errors, and displays a fallback UI.
 *
 * Must be a class component as React error boundaries require
 * componentDidCatch and getDerivedStateFromError lifecycle methods.
 *
 * Features:
 * - Catches errors in child components
 * - Displays customizable fallback UI
 * - Supports reset functionality via resetKeys or manual reset
 * - Provides error information to fallback components
 * - Calls onError callback for logging/reporting
 *
 * @example
 * ```tsx
 * <ErrorBoundary
 *   FallbackComponent={ErrorFallback}
 *   onError={(error, info) => logError(error, info)}
 * >
 *   <MyComponent />
 * </ErrorBoundary>
 * ```
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/buttons/Button";
import type {
  ErrorBoundaryProps,
  ErrorBoundaryState,
  FallbackProps,
} from "./types";

export class ErrorBoundary extends Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static displayName = "ErrorBoundary";

  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log error in development
    if (import.meta.env.DEV) {
      console.error("[ErrorBoundary] Caught error:", error);
      console.error(
        "[ErrorBoundary] Component stack:",
        errorInfo.componentStack,
      );
    }

    // Call onError callback if provided
    this.props.onError?.(error, errorInfo);
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    const { resetKeys } = this.props;
    const { hasError } = this.state;

    // Reset error state if resetKeys have changed
    if (hasError && resetKeys && prevProps.resetKeys) {
      const hasKeyChanged = resetKeys.some(
        (key, index) => key !== prevProps.resetKeys?.[index],
      );

      if (hasKeyChanged) {
        this.resetErrorBoundary();
      }
    }
  }

  resetErrorBoundary = (): void => {
    this.props.onReset?.();
    this.setState({
      hasError: false,
      error: null,
    });
  };

  render(): ReactNode {
    const { hasError, error } = this.state;
    const { children, FallbackComponent, fallback } = this.props;

    if (hasError && error) {
      // Use FallbackComponent if provided
      if (FallbackComponent) {
        const fallbackProps: FallbackProps = {
          error,
          resetErrorBoundary: this.resetErrorBoundary,
        };
        return <FallbackComponent {...fallbackProps} />;
      }

      // Use fallback ReactNode if provided
      if (fallback !== undefined) {
        return fallback;
      }

      // Default fallback
      return (
        <div role="alert" data-testid="error-boundary-fallback">
          <h2>Something went wrong</h2>
          <pre style={{ whiteSpace: "pre-wrap" }}>{error.message}</pre>
          <Button variant="primary" size="sm" onClick={this.resetErrorBoundary}>
            Try again
          </Button>
        </div>
      );
    }

    return children;
  }
}
